import { type FC } from 'react'
import { Radio as RadioMu, type RadioProps } from '@mui/material'
import { getAriaLabel } from 'shared/lib'
import { type ITooltipProps, Tooltip } from 'shared/ui/Tooltip'
import { Icon, type IIconsVector } from '../../Icon'

const sizer = {
  small: {
    display: 'flex',
    height: '14px',
    width: '14px',
    padding: '0px',
  },
  medium: {
    display: 'flex',
    height: '17px',
    width: '18px',
    padding: '0px',
  },
  large: {
    display: 'flex',
    height: '32px',
    width: '32px',
    padding: '0px',
  },
}

type IRadioSize = 'small' | 'medium' | 'large'
type IRadioProps = Omit<RadioProps, 'size'> & {
  size?: IRadioSize
  onChangeValue?: (value: boolean) => void
  tooltipProps?: ITooltipProps
}
type IIconWrapProps = {
  icon: IIconsVector
  size: number
}

const IconWrap: FC<IIconWrapProps> = ({ icon, size }) => {
  return <Icon icon={icon} fontSize={size} />
}

export const Radio = (props: IRadioProps) => {
  const { size = 'medium', onChangeValue, tooltipProps, ...otherProps } = props
  const ariaLabel = 'Radio'
  const sizePx = size === 'small' ? 14 : 18

  const radioContent = (
    <RadioMu
      disableRipple
      checkedIcon={<IconWrap icon={'radioboxChecked'} size={sizePx} />}
      icon={<IconWrap icon={'radiobox'} size={sizePx} />}
      onChange={(e) => {
        onChangeValue?.(e.target.checked)
      }}
      {...otherProps}
      sx={{
        backgroundColor: 'var(--background-primary-inverted-primary)',
        ...sizer[size],
        '&.MuiRadio-root': {
          color: 'var(--interaction-neutral-hovered)',
          '&:hover': {
            color: 'var(--interaction-neutral-primary)',
          },
        },
        '&.Mui-checked': {
          color: 'var(--action-contained-primary-normal)',
          '&:hover': {
            color: 'var(--action-contained-primary-hovered)',
          },
        },
        '&.Mui-disabled': {
          color: otherProps.checked
            ? 'var(--action-contained-primary-disabled)'
            : 'var(--border-neutral-default)',
          background: 'var(--background-neutral-default)',
        },
      }}
      aria-label={getAriaLabel(ariaLabel)}
    />
  )

  return tooltipProps ? <Tooltip {...tooltipProps}>{radioContent}</Tooltip> : radioContent
}
